import { roleColumnDataIndex } from './lib/util';
import Column from 'antd/lib/table/Column';
import sorters from './lib/sorters';
import ColumnTitle from './ColumnTitle';
import type {
  RoleInformation,
  RosterColumnHeader,
  RosterDisplayMode,
  RosterTableRowRecord,
  RosterUserRecord,
  ShiftPeriod,
} from './types';
import RosterCell from './RosterCell';
import { defaultDayShiftPeriod } from '../Teams/TeamEditor/util';
import rfsTheme, { shiftBackgroundColours } from '../../theme';
import { Table, TablePaginationConfig } from 'antd';
import { parse, subDays } from 'date-fns';
import { shiftDateDataIndexFormat } from '../../lib/utils/date-time';

/**
 * The z-index of role name column.
 * Increase the z-index to make sure it will not covered by other columns.
 */
const roleColumnZIndex = 3;

const roleColumnHeaderZIndex = 11;

/**
 * Min width for role column cell in pixel.
 */
const roleColumnCellMinWidth = 250;

/**
 * Min width for shifts cell in pixel.
 */
const shiftCellMinWidth = 85;

const rosterCellBackgroundColour = (col: RosterColumnHeader) => {
  if (col.isToday) {
    return rfsTheme.darkRfsRedBackground;
  }

  if (col.isWeekend) {
    return rfsTheme.rosterWeekendShade;
  }

  // Set white background to team role column to avoid
  // overlapping of role name and shift button background pattern.
  if (col.dataIndex === roleColumnDataIndex) {
    return 'white';
  }

  return 'none';
};

const rosterCellMinWidth = (col: RosterColumnHeader) => {
  if (col.dataIndex === roleColumnDataIndex) {
    return `${roleColumnCellMinWidth}px`;
  }

  return `${shiftCellMinWidth}px`;
};

type RosterTableProps = {
  readonly tableHeaderOffset?: number;
  readonly loading: boolean;
  readonly rosterShiftsRecords: readonly RosterTableRowRecord[];
  readonly rosterPaginationSetting: false | TablePaginationConfig;
  readonly rosterDisplayMode: RosterDisplayMode;
  readonly rosterColumnHeaders: readonly RosterColumnHeader[];
  readonly readOnly: boolean;
  readonly dayShiftPeriod?: ShiftPeriod;
  readonly manageRosterPermission: boolean;
  readonly rolesInfo: readonly RoleInformation[];
  readonly showShiftStatus: boolean;
};

/**
 * Table that shows the rostered shifts for either 1 or 4 weeks
 *
 * @param tableHeaderOffset pixels, the space between the header and table, used in personal and team modes
 * @param loading boolean, will show a spinner when loading
 * @param rosterShiftsRecords that are the data that will be shown in the table
 * @param rosterPaginationSetting to be passed down into the table
 * @param rosterDisplayMode either 1 week or 4 weeks
 * @param rosterColumnHeaders the headers that are shown in the table, (team and dates)
 * @param personalRosterMode boolean for if the table is in personal mode
 * @param dayShiftPeriod the start, and end time of the day shift. Night shift is the other time
 * @param manageRosterPermission if the table should show in this mode
 * @param rolesInfo information on the roles shown
 * @param showShiftStatus whether to show shift status on shift button
 */
export const RosterTable = ({
  tableHeaderOffset,
  loading,
  rosterShiftsRecords,
  rosterPaginationSetting,
  rosterDisplayMode,
  rosterColumnHeaders,
  readOnly,
  dayShiftPeriod,
  manageRosterPermission,
  rolesInfo,
  showShiftStatus,
}: RosterTableProps) => {
  // First roster date (from date). It is second entry of the columns with first being the Role & Personnel column.
  const firstRosterDateDataIndex = rosterColumnHeaders[1]?.dataIndex;

  const firstRosterDate =
    firstRosterDateDataIndex === undefined
      ? new Date()
      : parse(firstRosterDateDataIndex, shiftDateDataIndexFormat, new Date());

  // The roster's first date's previous day is a buffer date that will be used to render any overflow shifts.
  const bufferFirstRosterDate = subDays(new Date(firstRosterDate), 1);

  return (
    <Table
      bordered
      sticky={{
        offsetHeader: tableHeaderOffset,
      }}
      className="roster-table"
      loading={loading}
      tableLayout="fixed"
      dataSource={rosterShiftsRecords}
      rowKey={'key'}
      pagination={rosterPaginationSetting}
      expandable={{
        defaultExpandAllRows: true,
        expandedRowKeys: rosterShiftsRecords.map((record) => record.key),
      }}
      scroll={{
        x:
          rosterDisplayMode === 'monthly'
            ? // Set a specific width to table when there is no data to avoid squashed column issue.
              rosterShiftsRecords.length === 0
              ? `${
                  rosterColumnHeaders.length * shiftCellMinWidth +
                  roleColumnCellMinWidth -
                  shiftCellMinWidth
                }px`
              : true
            : undefined,
      }}
    >
      {rosterColumnHeaders.map((col, index) => {
        // The role header is the first header which allows you to sort by roles
        // Otherwise the header is a simple date
        const isRoleColumn = col.dataIndex === roleColumnDataIndex;

        const columnZIndex = isRoleColumn ? roleColumnZIndex : undefined;

        const columnHeaderZIndex = isRoleColumn
          ? roleColumnHeaderZIndex
          : undefined;

        return (
          <Column
            key={index}
            defaultSortOrder={'ascend'}
            sorter={sorters(col.dataIndex)}
            // Adding another ascend to the list to make sure the sorting is required, so it will not reset the sorting.
            sortDirections={['ascend', 'descend', 'ascend']}
            align="left"
            width={isRoleColumn ? `${roleColumnCellMinWidth}px` : undefined}
            fixed={isRoleColumn}
            title={
              <span
                aria-label={
                  isRoleColumn
                    ? `Toggle sort by role assignments order`
                    : undefined
                }
                role={isRoleColumn ? 'button' : undefined}
              >
                <ColumnTitle
                  className={
                    isRoleColumn ? 'role-col-header' : 'date-col-header'
                  }
                  title={col.title}
                  isToday={col.isToday}
                />
              </span>
            }
            onHeaderCell={(data) => {
              // First column index.
              if (data.key === '0') {
                return { style: { zIndex: columnHeaderZIndex } };
              }

              return {};
            }}
            onCell={() => ({
              style: {
                overflow: col.isLastColumn ? 'hidden' : undefined,
                background: rosterCellBackgroundColour(col),
                minWidth: rosterCellMinWidth(col),
                zIndex: columnZIndex,
                borderRight:
                  col.dataIndex === roleColumnDataIndex
                    ? '2px solid #d9d9d9'
                    : 'none',
              },
            })}
            render={(record: RosterTableRowRecord & RosterUserRecord) => (
              <RosterCell
                rosterDisplayMode={rosterDisplayMode}
                rosterTableRowRecord={record}
                dataIndex={col.dataIndex}
                readOnly={readOnly}
                dayShift={dayShiftPeriod ?? defaultDayShiftPeriod}
                shiftBackgroundColours={shiftBackgroundColours}
                allowAssignUser={manageRosterPermission}
                roleInformation={rolesInfo}
                bufferFirstRosterDate={bufferFirstRosterDate}
                showShiftStatus={showShiftStatus}
              />
            )}
          />
        );
      })}
    </Table>
  );
};

export default RosterTable;
